// patient.resolver.ts
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Resolve } from "@angular/router";
import { FileLogger } from "@comunicare/comunicare-helper";
import { IConfiguration } from "@comunicare/comunicare-interfaces";
import { Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Base64Helper } from "src/app/helpers/Base64Helper";
import { PatientService } from "../patient.service";

/**
 * Resolver for the Patient Page.
 *
 * This resolver is executed before the Patient Page is loaded. It ensures that the
 * patient's configuration is fetched and up-to-date before any other calls are made
 * within the patient file.
 *
 * Why is this necessary?
 * The configuration endpoint (/dashboard/configuration) defines critical patient-specific settings,
 * including observation parameters, reference (default) values, and other metadata.
 * These settings must be loaded first so that all subsequent calls made within the
 * patient page (observations, careplans, reference value etc.) rely on accurate and up-to-date data.
 * Loading the page without this configuration could result in incorrect default values
 * or missing parameters being used across child components.
 *
 * Future improvements:
 * - The /patientUser endpoint should also be called here (once), and its value
 *   propagated to all components that need it, instead of being called multiple
 *   times across different components. This would reduce redundant HTTP calls
 *   and ensure consistency throughout the patient file.
 *   https://comunicare.atlassian.net/browse/CMATE-8236
 */
@Injectable({ providedIn: "root" })
export class PatientPageResolver implements Resolve<any> {
  constructor(private patientService: PatientService) {}

  resolve(route: ActivatedRouteSnapshot): Observable<IConfiguration | null> {
    try {
      const caremateIdentifier = Base64Helper.b64_to_utf8(route.params["id"]);
      return this.patientService.getPatientConfiguration(caremateIdentifier).pipe(
        tap(() => {
          FileLogger.log("PatientPageResolver", "getPatientConfiguration - call", "", "none");
        }),
        catchError((err) => {
          FileLogger.error("PatientPageResolver", "getPatientConfiguration", err, "none");
          return of(null);
        })
      );
    } catch (error) {
      FileLogger.error("PatientPageResolver", "resolve", error, "none");
      return of(null);
    }
  }
}
